// src/Components/ScheduledSearchCreate.js
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Typography, Box, Paper, Switch, TextField, FormControl, RadioGroup,
  FormControlLabel, Radio, Tooltip, Button, CircularProgress, MenuItem, Select,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { WithContext as ReactTags } from "react-tag-input";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import PortalPicker from "./PortalPicker";

const apiPrefix = window.location.hostname === "localhost" ? "" : "/api";
const KeyCodes = { comma: 188, enter: 13 };
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const SectionHeader = ({ title, tooltip, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 4 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem' }}
    >
      {title}
    </Typography>
    {tooltip && (
      <Tooltip title={tooltip} placement="right">
        <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Tooltip>
    )}
    {children}
  </Box>
);

export default function ScheduledSearchCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.user_id;
  const { showError, showWarning } = useNotification();

  useEffect(() => { document.title = 'Nova pretraga | Media Scanner'; }, []);

  const nameRef = useRef(null);
  useEffect(() => { nameRef.current?.focus(); }, []);

  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [tags, setTags] = useState([]);
  const [excludingTags, setExcludingTags] = useState([]);
  const [emailTags, setEmailTags] = useState([]);
  const [selectedPortals, setSelectedPortals] = useState([]);
  const [frequency, setFrequency] = useState('daily');
  const [executionDay, setExecutionDay] = useState(0);
  const [executionTime, setExecutionTime] = useState('09:00');
  const [timeSpan, setTimeSpan] = useState(12);
  const [saving, setSaving] = useState(false);

  const dayLabels = ['Ponedjeljak', 'Utorak', 'Srijeda', 'Četvrtak', 'Petak', 'Subota', 'Nedjelja'];

  const handleDeleteTag = (i) => setTags(tags.filter((_, idx) => idx !== i));
  const handleAddition = (tag) => setTags([...tags, { id: String(tag.id ?? tag.text), text: String(tag.text) }]);
  const handleDrag = (tag, currPos, newPos) => {
    const next = tags.slice();
    next.splice(currPos, 1);
    next.splice(newPos, 0, tag);
    setTags(next);
  };

  const handleDeleteExcludingTag = (i) => setExcludingTags(excludingTags.filter((_, idx) => idx !== i));
  const handleAdditionExcluding = (tag) => setExcludingTags([...excludingTags, { id: String(tag.id ?? tag.text), text: String(tag.text) }]);

  const isFormValid = name.trim() && tags.length > 0 && selectedPortals.length > 0 && emailTags.length > 0;

  const handleSave = async () => {
    if (!user) { showWarning('Korisnik nije prijavljen'); return; }
    if (!name.trim()) { showWarning('Naziv pretrage je obavezno polje'); return; }
    if (tags.length === 0) { showWarning('Dodajte barem jedan pojam za pretragu'); return; }
    if (selectedPortals.length === 0) { showWarning('Odaberite barem jedan portal'); return; }
    if (emailTags.length === 0) { showWarning('Dodajte barem jednu email adresu za dostavu rezultata'); return; }

    setSaving(true);
    try {
      const res = await axios.post(
        `${apiPrefix}/createScheduledSearch`,
        {
          users_id: userId,
          name: name.trim(),
          is_active: isActive,
          is_archived: false,
          frequency,
          execution_day: frequency === 'weekly' ? executionDay : null,
          execution_time: `${executionTime}:00`,
          time_span: Number(timeSpan),
        },
        { withCredentials: true }
      );

      if (res.status !== 201 || !res.data?.id) {
        showError('Greška pri kreiranju pretrage. Pokušajte ponovo.');
        return;
      }
      const newId = res.data.id;

      if (tags.length > 0) {
        await axios.post(
          `${apiPrefix}/checkAndSaveTermsForScheduledSearch`,
          {
            user_id: userId,
            scheduled_search_id: newId,
            terms: tags.map(t => ({ id: String(t.id ?? t.text ?? ''), text: String(t.text ?? '') })),
          },
          { withCredentials: true }
        );
      }

      if (selectedPortals.length > 0) {
        await axios.post(
          `${apiPrefix}/checkAndSavePortalsForScheduledSearch`,
          { user_id: userId, scheduled_search_id: newId, portal_ids: selectedPortals.map(p => p.id) },
          { withCredentials: true }
        );
      }

      await axios.post(
        `${apiPrefix}/checkAndSaveEmailsForScheduledSearch`,
        { user_id: userId, scheduled_search_id: newId, email_addresses: emailTags.map(t => t.text) },
        { withCredentials: true }
      );

      await axios.post(
        `${apiPrefix}/checkAndSaveExcludingTermsForScheduledSearch`,
        {
          user_id: userId,
          scheduled_search_id: newId,
          terms: excludingTags.map(t => ({ id: String(t.id ?? t.text ?? ''), text: String(t.text ?? '') })),
        },
        { withCredentials: true }
      );

      navigate('/scheduledSearches');
    } catch (e) {
      console.error('Create scheduled search failed:', e);
      showError('Greška pri kreiranju pretrage. Pokušajte ponovo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
        {/* Page header */}
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 3 }}>
          Nova automatizirana pretraga
        </Typography>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {/* Name & Status */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>Naziv</Typography>
          <TextField
            fullWidth
            inputRef={nameRef}
            placeholder="Unesi naziv pretrage"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!name.trim()}
            helperText={!name.trim() ? 'Naziv je obavezno polje' : ''}
            size="small"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>Status:</Typography>
            <Typography
              variant="caption"
              sx={{
                px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                backgroundColor: isActive ? '#e8f5e9' : '#f5f5f5',
                color: isActive ? '#27ae60' : '#999',
              }}
            >
              {isActive ? 'Aktivna' : 'Neaktivna'}
            </Typography>
            <Switch size="small" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />
          </Box>

          {/* Schedule */}
          <SectionHeader title="Učestalost" />
          <FormControl>
            <RadioGroup row value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              <FormControlLabel value="daily" control={<Radio />} label="Svakodnevno" />
              <FormControlLabel value="weekly" control={<Radio />} label="Tjedno" />
            </RadioGroup>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
            {frequency === 'weekly' && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#2c3e50' }}>
                  Dan u tjednu
                </Typography>
                <Select value={executionDay} onChange={(e) => setExecutionDay(e.target.value)} size="small" sx={{ minWidth: 160 }}>
                  {dayLabels.map((label, i) => (
                    <MenuItem key={i} value={i}>{label}</MenuItem>
                  ))}
                </Select>
              </Box>
            )}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600, color: '#2c3e50' }}>
                Vrijeme pokretanja
              </Typography>
              <TextField type="time" value={executionTime} onChange={(e) => setExecutionTime(e.target.value)} inputProps={{ step: 300 }} size="small" />
            </Box>
            <Box>
              <Tooltip title="Koliko sati unazad treba pretražiti (npr. 12 = zadnjih 12 sati)" placement="right">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>Pretraži unazad (sati)</Typography>
                  <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Box>
              </Tooltip>
              <TextField type="number" value={timeSpan} onChange={(e) => setTimeSpan(e.target.value)} inputProps={{ min: 1, max: 168 }} size="small" sx={{ width: 120 }} />
            </Box>
          </Box>

          {/* Terms */}
          <SectionHeader title="Pojmovi" />
          <ReactTags
            tags={tags}
            suggestions={[]}
            delimiters={delimiters}
            handleDelete={handleDeleteTag}
            handleAddition={handleAddition}
            handleDrag={handleDrag}
            handleTagClick={() => {}}
            inputFieldPosition="bottom"
            placeholder="Unesi pojam, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Excluding terms */}
          <SectionHeader
            title="Isključujući pojmovi"
            tooltip="Članci koji sadrže ove pojmove neće biti prikazani u rezultatima pretrage"
          />
          <ReactTags
            tags={excludingTags}
            suggestions={[]}
            delimiters={delimiters}
            handleDelete={handleDeleteExcludingTag}
            handleAddition={handleAdditionExcluding}
            handleTagClick={() => {}}
            inputFieldPosition="bottom"
            placeholder="Unesi pojam za isključivanje, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Portals */}
          <SectionHeader title="Portali" />
          <PortalPicker value={selectedPortals} onChange={setSelectedPortals} showChips={false} />

          {/* Email addresses */}
          <SectionHeader title="Email adrese za dostavu rezultata" />
          <ReactTags
            tags={emailTags}
            suggestions={[]}
            delimiters={delimiters}
            handleDelete={(i) => setEmailTags(emailTags.filter((_, idx) => idx !== i))}
            handleAddition={(tag) => setEmailTags([...emailTags, tag])}
            handleTagClick={() => {}}
            inputFieldPosition="bottom"
            placeholder="Unesi email adresu, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/scheduledSearches')}
            >
              Odustani
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !isFormValid}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Kreiranje...' : 'Kreiraj pretragu'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
